import { Location } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Event, NavigationStart, Router, RouterEvent } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { CanDeactivateComponent } from '@app/interfaces';
import { ApplicantIndividualMessage } from '@app/models/recruiting/applicant-individual-message.model';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { ApplicantIndividualTestMessage } from '@app/modules/applicant-tracker/interfaces/bulk-applicant-message';
import { ApplicantMessageService } from '@app/modules/applicant-tracker/services';
import { ApplicantIndividualMessageForm } from '@app/modules/forms/applicant-tracker/components';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
    templateUrl: './applicant-individual-message.template.html',
})
export class ApplicantIndividualMessageView implements CanDeactivateComponent, OnDestroy {
    applicant: Applicant = this.route.snapshot.data.models.get('applicant');
    routerSubsription: Subscription;
    isLoading = false;
    isDeactivateModalOpen = false;
    canNavigateBack = true;
    navigationPath: string;

    applicantIndividualMessage: ApplicantIndividualMessage = new ApplicantIndividualMessage({
        replyTo: this.auth.account.email,
        isBulk: false,
        atsApplicantId: this.applicant.id,
    });

    constructor(
        private auth: AuthService,
        private route: ActivatedRoute,
        private applicantMessageService: ApplicantMessageService,
        private notifyService: NotifyService,
        private location: Location,
        private router: Router,
        private analyticService: AnalyticService
    ) {
        this.routerSubsription = router.events
            .pipe(filter((e: Event | RouterEvent): e is RouterEvent => e instanceof NavigationStart))
            .subscribe((e: NavigationStart) => {
                this.navigationPath = e.url;
            });
    }

    canDeactivate(): boolean {
        if (this.applicantIndividualMessage.body) {
            this.canNavigateBack = false;
            this.isDeactivateModalOpen = true;
            return;
        }

        return this.canNavigateBack;
    }

    canNavigateAway(): void {
        this.canNavigateBack = true;
        this.applicantIndividualMessage.body = '';
        this.router.navigateByUrl(this.navigationPath);
    }

    openDeactivateModalOpen(): void {
        this.isDeactivateModalOpen = true;
    }

    closeDeactivateModalOpen(): void {
        this.isDeactivateModalOpen = false;
    }

    async onSave(applicantMessageForm: ApplicantIndividualMessageForm): Promise<void> {
        applicantMessageForm.submit();

        if (!applicantMessageForm.valid) {
            return;
        }

        this.isLoading = true;

        try {
            await this.applicantIndividualMessage.save();
            this.notifyService.success('applicant-tracker.emailSent');
            this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantProfileEmailApplicantSend);
            this.applicantIndividualMessage.body = '';
            this.canNavigateBack = true;
            this.navigationPath ? this.router.navigateByUrl(this.navigationPath) : this.location.back();
        } catch (err) {
            this.notifyService.error('applicant-tracker.emailFailedToSend');
            this.location.back();
        }

        this.isLoading = false;
    }

    async onSendTest(applicantMessageForm: ApplicantIndividualMessageForm): Promise<void> {
        applicantMessageForm.submit();

        if (!applicantMessageForm.valid) {
            return;
        }

        const applicantTestMessage: ApplicantIndividualTestMessage = {
            atsJobId: this.applicant.jobPostingId,
            body: this.applicantIndividualMessage.body,
            replyTo: this.applicantIndividualMessage.replyTo,
            subject: this.applicantIndividualMessage.subject,
        };

        this.isLoading = true;

        try {
            await this.applicantMessageService.onApplicantIndividualTestMessage(applicantTestMessage);
            this.notifyService.success({
                key: 'applicant-tracker.testEmailSentTo',
                params: {
                    email: this.auth.account.email,
                },
            });
            this.analyticService.trackEvent(AnalyticEvents.RecruitingAllEmailsSendTest);
        } catch (err) {
            this.notifyService.error({
                key: 'applicant-tracker.testEmailFailedToSendTo',
                params: {
                    email: this.auth.account.email,
                },
            });
        }

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.routerSubsription.unsubscribe();
    }
}
